import React from 'react';
import { Link } from 'react-router-dom';
import { HelpCircle, Shield, CreditCard, Video, Users, AlertCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const faqCategories = [
  {
    title: 'Privacidad y Anonimato',
    icon: Shield,
    faqs: [
      {
        question: '¿Realmente es anónimo el servicio?',
        answer: 'Sí, 100%. No solicitamos tu nombre real, RUT, cédula de identidad ni ningún dato que te identifique. Solo necesitas elegir un seudónimo (nombre ficticio) para tu sesión. Tu psicólogo solo conocerá ese seudónimo.'
      },
      {
        question: '¿Mi psicólogo sabrá quién soy?',
        answer: 'No. Tu psicólogo solo conocerá el seudónimo que elijas. No tiene acceso a tu identidad real, datos de pago completos, ni ubicación. Está comprometido éticamente a mantener la confidencialidad de la sesión.'
      },
      {
        question: '¿Se graban las sesiones?',
        answer: 'Nunca. Las sesiones de video no se graban bajo ninguna circunstancia. Lo que hablas con tu psicólogo queda únicamente entre ustedes. Ni AnonimaMente ni ningún tercero tiene acceso al contenido de las sesiones.'
      },
      {
        question: '¿Puedo mantener mi cámara apagada?',
        answer: 'Sí, la cámara es completamente opcional. Puedes participar solo con audio si así lo prefieres. Muchos usuarios eligen esta opción para mantener su anonimato visual.'
      },
      {
        question: '¿Qué datos almacenan sobre mí?',
        answer: 'Solo almacenamos el mínimo necesario: tu seudónimo, fecha/hora de sesiones, y datos de pago (que no revelan tu identidad si pagas con Bitcoin). No guardamos contenido de sesiones, diagnósticos, ni historial clínico.'
      }
    ]
  },
  {
    title: 'Pagos y Facturación',
    icon: CreditCard,
    faqs: [
      {
        question: '¿Qué métodos de pago aceptan?',
        answer: 'Aceptamos Webpay (débito y crédito), Flow, Khipu (transferencia bancaria), y Bitcoin (via BTCPay Server). El pago con Bitcoin ofrece la máxima privacidad ya que no requiere datos bancarios personales.'
      },
      {
        question: '¿Se emite boleta o factura?',
        answer: 'Sí, emitimos boleta electrónica por cada sesión, independientemente del método de pago. Si necesitas factura para tu empresa, podemos emitirla indicando solo el servicio "consulta psicológica" sin detalles personales.'
      },
      {
        question: '¿Puedo pedir reembolso?',
        answer: 'Sí, puedes cancelar con hasta [X] horas de anticipación para reembolso completo. Cancelaciones tardías están sujetas a la política del profesional. En caso de problemas técnicos imputables a la plataforma, siempre se ofrece reagendamiento o reembolso.'
      },
      {
        question: '¿Cómo funciona el pago con Bitcoin?',
        answer: 'Usamos BTCPay Server, una solución sin custodia. Generas un pago desde tu billetera Bitcoin, el monto se convierte a CLP al momento del pago, y la sesión se confirma automáticamente. Es la opción más privada ya que no requiere datos bancarios.'
      }
    ]
  },
  {
    title: 'Sesiones y Plataforma',
    icon: Video,
    faqs: [
      {
        question: '¿Cómo me conecto a la sesión?',
        answer: 'Después de reservar, recibirás un enlace único de videollamada. Solo haz clic en el enlace 5-10 minutos antes de la hora. Usamos Jitsi Meet, que no requiere descargar ninguna aplicación ni crear cuenta.'
      },
      {
        question: '¿Necesito instalar algo?',
        answer: 'No. Las sesiones funcionan directamente en tu navegador web (Chrome, Firefox, Safari, Edge). No necesitas descargar aplicaciones ni crear cuentas en otras plataformas.'
      },
      {
        question: '¿Qué pasa si tengo problemas técnicos?',
        answer: 'Si tienes problemas de conexión, puedes reconectarte usando el mismo enlace. Si el problema persiste, contacta a soporte y te ayudaremos a reagendar sin costo adicional.'
      },
      {
        question: '¿Cuánto dura una sesión?',
        answer: 'Las sesiones individuales duran 50 minutos. Las sesiones de pareja duran entre 60-75 minutos. El tiempo está diseñado para permitir un trabajo terapéutico efectivo.'
      },
      {
        question: '¿Puedo cambiar de psicólogo?',
        answer: 'Sí, puedes cambiar de profesional en cualquier momento sin dar explicaciones. Simplemente agenda tu próxima sesión con otro psicólogo de nuestra red.'
      }
    ]
  },
  {
    title: 'Para Empresas',
    icon: Users,
    faqs: [
      {
        question: '¿La empresa puede saber quién usa el servicio?',
        answer: 'No. Aplicamos k-anonymity: solo entregamos reportes agregados cuando hay mínimo 10 usuarios. Nunca identidades individuales. La empresa ve tendencias y estadísticas, no personas.'
      },
      {
        question: '¿Qué incluyen los reportes para empresas?',
        answer: 'Los reportes incluyen: número de sesiones utilizadas, áreas de mayor demanda (ansiedad, estrés, etc.), satisfacción general, y tendencias de uso. Todo agregado, sin identificar individuos.'
      },
      {
        question: '¿Cómo se implementa el programa?',
        answer: 'La implementación toma 3-4 semanas. Incluye: configuración de la cuenta corporativa, capacitación al equipo de RRHH, material de comunicación interna, y landing page personalizada para tus colaboradores.'
      },
      {
        question: '¿Cuál es el mínimo de colaboradores?',
        answer: 'Trabajamos con empresas desde 20 colaboradores. Para organizaciones más pequeñas, ofrecemos bolsas de sesiones prepagadas como alternativa.'
      }
    ]
  },
  {
    title: 'Emergencias y Derivaciones',
    icon: AlertCircle,
    faqs: [
      {
        question: '¿Es un servicio de emergencias?',
        answer: 'No. AnonimaMente no es un servicio de emergencias ni crisis. Si necesitas ayuda inmediata, contacta: Salud Responde (600 360 7777), Fono Ayuda (*4141), o acude a urgencias del hospital más cercano.'
      },
      {
        question: '¿Qué pasa si necesito atención presencial?',
        answer: 'Tu psicólogo puede orientarte sobre cuándo es recomendable atención presencial y ayudarte con una derivación responsable. El servicio online complementa, no reemplaza, la atención presencial cuando esta es necesaria.'
      },
      {
        question: '¿Los psicólogos pueden recetar medicamentos?',
        answer: 'No. Los psicólogos no prescriben medicamentos (eso lo hacen psiquiatras). Si tu psicólogo considera que podrías beneficiarte de evaluación psiquiátrica, te orientará para una derivación adecuada.'
      }
    ]
  }
];

const FAQPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero */}
      <section className="bg-teal-600 py-16 md:py-24">
        <div className="container-max mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <Badge className="bg-white/20 text-white hover:bg-white/20 mb-4">
              <HelpCircle className="w-3 h-3 mr-1" />
              Centro de Ayuda
            </Badge>
            <h1 className="font-heading text-3xl md:text-5xl font-semibold text-white mb-6">
              Preguntas frecuentes
            </h1>
            <p className="text-lg text-teal-100 leading-relaxed">
              Resolvemos tus dudas sobre privacidad, pagos, sesiones y más. 
              Si no encuentras lo que buscas, contáctanos.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="section-padding">
        <div className="container-max mx-auto">
          <div className="max-w-4xl mx-auto space-y-12">
            {faqCategories.map((category, catIndex) => (
              <div key={catIndex}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center">
                    <category.icon className="w-5 h-5 text-teal-600" />
                  </div>
                  <h2 className="font-heading text-2xl font-semibold text-slate-900">
                    {category.title}
                  </h2>
                </div>

                <Accordion type="single" collapsible className="space-y-3">
                  {category.faqs.map((faq, faqIndex) => (
                    <AccordionItem 
                      key={faqIndex} 
                      value={`cat-${catIndex}-faq-${faqIndex}`}
                      className="bg-white rounded-xl border-0 shadow-soft px-6"
                    >
                      <AccordionTrigger className="text-left font-medium text-slate-900 hover:no-underline py-5">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-slate-600 pb-5 leading-relaxed">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency Notice */}
      <section className="py-8 bg-amber-50 border-y border-amber-200">
        <div className="container-max mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-center md:text-left">
            <AlertCircle className="w-8 h-8 text-amber-600 shrink-0" />
            <div>
              <p className="font-semibold text-amber-900">¿Necesitas ayuda urgente?</p>
              <p className="text-amber-800">
                Este no es un servicio de emergencias. Contacta: 
                <a href="tel:600 360 7777" className="font-bold ml-1 hover:underline">Salud Responde: 600 360 7777</a>
                <span className="mx-2">|</span>
                <a href="tel:*4141" className="font-bold hover:underline">Fono Ayuda: *4141</a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-white">
        <div className="container-max mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-slate-900 mb-4">
            ¿No encontraste tu respuesta?
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-8">
            Nuestro equipo está disponible para ayudarte con cualquier duda adicional.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contacto">
              <Button className="btn-primary">
                Contactar soporte
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/agendar">
              <Button variant="outline" className="btn-secondary">
                Agendar sesión
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
